/**
 * Single-flight wrapper around Amplify's Cognito Identity-Pool credentials
 * provider.
 *
 * Amplify's default provider (CognitoAWSCredentialsAndIdentityIdProvider) keeps a
 * 50-min in-memory credential cache but has NO in-flight de-duplication: on any
 * cache-empty moment — TTL expiry, `forceRefresh`, or an idToken rotation
 * (`hasTokenChanged`) — it clears the cache and THEN awaits
 * GetCredentialsForIdentity, so N concurrent callers each fire their own network
 * call. Every `authMode: "userPool"` GraphQL request calls the full
 * `fetchAuthSession()` internally (@aws-amplify/api-graphql graphqlAuth), which
 * runs this credential leg even though it only needs the JWT — so a react-query
 * refetch-on-focus burst (a whole page of queries at once) stampedes the
 * Identity Pool and trips its ~50 req/s limit (TooManyRequestsException: Rate
 * exceeded). See Amplify issues #12839 / #13565 / #13608.
 *
 * This wrapper coalesces concurrent `getCredentialsAndIdentityId` calls onto a
 * single in-flight promise, so a burst of parallel GraphQL requests shares ONE
 * GetCredentialsForIdentity instead of dozens. It changes nothing about caching
 * or refresh semantics — it only collapses the concurrent burst; a settled call
 * frees the slot so the next request resolves via the provider's own cache.
 *
 * A force-refresh caller may NOT reuse an in-flight non-force call (a non-force
 * call can resolve to cached creds and would deny the force caller the refresh
 * it needs), mirroring AuthSessionProvider.configureAuthSession's single-flight
 * guard.
 *
 * It also self-heals a stale identity id (see `isStaleIdentityIdError`): when a
 * signed-in fetch is rejected because this tab's cached identity id belongs to
 * another user, it clears the cached id + credentials and retries once, so
 * `GetId` re-resolves the id from the current tokens. Without this, the error
 * reads as a dead session and the tab drops into expired-session handling even
 * though the login is valid. The retry runs inside the coalesced promise, so
 * concurrent callers share the healed result.
 */
import type {
  CredentialsAndIdentityId,
  CredentialsAndIdentityIdProvider,
  GetCredentialsOptions,
} from "@aws-amplify/core";
import { cognitoCredentialsProvider } from "aws-amplify/auth/cognito";
import { Hub } from "aws-amplify/utils";
import { isStaleIdentityIdError } from "~/auth/authErrors";

// Dispatched on the Hub "application" channel after a heal succeeds, so
// AuthSessionProvider can report it. Reporting from here directly would import
// errorReporter → common/utils → lib/amplify-client → this module: a cycle at
// the app's first import.
export const STALE_IDENTITY_RECOVERED_EVENT = "staleIdentityId_recovered";

let inFlight: Promise<CredentialsAndIdentityId | undefined> | null = null;
let inFlightIsForce = false;
// Guest vs signed-in identity-pool credentials are different results, so a call
// may only reuse an in-flight one that targets the same identity type. This app
// always requests authenticated creds, so in practice the slot is never split —
// but keying on it keeps the wrapper correct for a mixed burst.
let inFlightAuthenticated = false;

async function fetchWithStaleIdentityRecovery(
  options: GetCredentialsOptions,
): Promise<CredentialsAndIdentityId | undefined> {
  try {
    return await cognitoCredentialsProvider.getCredentialsAndIdentityId(
      options,
    );
  } catch (error) {
    // Signed-in calls only. With no tokens Amplify takes the guest path, which
    // fails differently ("Access to Identity … is forbidden") and means "no
    // session": healing it would mint a guest identity and hide a dead login
    // from the expired-session handling.
    if (!options.authenticated || !isStaleIdentityIdError(error)) {
      throw error;
    }
    // The underlying provider, not this wrapper's clear: that would free the
    // in-flight slot this retry is running in.
    await cognitoCredentialsProvider.clearCredentialsAndIdentityId();
    const healed =
      await cognitoCredentialsProvider.getCredentialsAndIdentityId(options);
    Hub.dispatch("application", { event: STALE_IDENTITY_RECOVERED_EVENT });
    return healed;
  }
}

export const singleFlightCredentialsProvider: CredentialsAndIdentityIdProvider =
  {
    getCredentialsAndIdentityId(
      options: GetCredentialsOptions,
    ): Promise<CredentialsAndIdentityId | undefined> {
      const force = options.forceRefresh ?? false;
      const authenticated = options.authenticated;

      // Reuse the in-flight call when it can satisfy this caller: it must target
      // the same identity type (guest vs signed-in), and a force caller may only
      // reuse an in-flight call that is itself a force refresh (a non-force one
      // could resolve to cached creds — not the refresh it asked for).
      if (
        inFlight &&
        inFlightAuthenticated === authenticated &&
        (!force || inFlightIsForce)
      ) {
        return inFlight;
      }

      const promise = fetchWithStaleIdentityRecovery(options).finally(() => {
        // Only free the slot if this call still owns it — a force (or
        // different-identity-type) call may have overtaken an earlier one and
        // taken the slot, so a slower promise settling later must not clobber
        // the newer owner.
        if (inFlight === promise) {
          inFlight = null;
          inFlightIsForce = false;
        }
      });

      inFlight = promise;
      inFlightIsForce = force;
      inFlightAuthenticated = authenticated;
      return promise;
    },

    clearCredentialsAndIdentityId(): void {
      // Drop the coalesced slot so the next caller re-fetches, then delegate to
      // the real provider (which clears its in-memory + persisted identity id).
      inFlight = null;
      inFlightIsForce = false;
      void cognitoCredentialsProvider.clearCredentialsAndIdentityId();
    },
  };
